import { DATA_SOURCES, TIKTOK_COUNTRIES, isCovered } from '@/config/constants';
import { parentNameFilterKeyForRouteSlug } from 'utils/dataSources';
import { InstagramRelatedCategoriesSectionTable } from 'components/DataVis/InstagramRelatedCategoriesTable';
import { RelatedCategoriesSectionTable } from 'components/DataVis/RelatedCategoriesTable';
import {
    useAbortController,
    useCurrentCountry,
    useEndDate,
    useTikTokWeeks,
} from 'utils/hooks';
import { useLocation, useParams } from 'react-router-dom';
import { DataSourceBreadCrumbs } from 'components';
import DataSourceTabsByCoverage from 'components/DataSourceTabsByCoverage';
import ExploreRelatedSearchesGroupTable from 'components/DataVis/AffinitiesTable/ExploreRelatedSearchesGroupTable';
import { IndustriesPlaceholderMessage } from 'components/Placeholders';
import NoDataSourceMarketCoverage from 'components/NoDataSourceMarketCoverage';
import NoTikTokDataSection from 'components/NoTikTokDataSection';
import PageBar from 'components/PageBar';
import { PagePadding } from 'components/PagePadding';
import PopularityIndexRelatedEntitiesTable from 'components/DataVis/PopularityIndexRelatedEntitiesTable/PopularityIndexRelatedEntitiesTable';
import { Redirect } from 'react-router';
import { SectionCard } from 'components/SectionCard';
import SectionTableFilterChips from 'components/SectionTableFilterChips';
import Stack from 'components/Stack';
import useCountryParam from 'utils/hooks/useCountryParam';
import { useEntities } from 'utils/hooks/api';
import useInstagramWeeks from 'utils/hooks/useInstagramWeeks';
import { buildExportFilename } from 'utils/formatCSV';

function PopularityIndexRelatedCategories({
    category,
    name,
    affinityGroupFilters,
    filterParamSeparator,
    filterParamPrefix,
    filtersSlot,
    sortParamKey,
}) {
    const params = useParams();
    const categoryId = params.categoryId;
    const country = useCurrentCountry();
    const { week, year } = useTikTokWeeks();
    const date = useEndDate('pop-index');

    return (
        <PopularityIndexRelatedEntitiesTable
            categories={categoryId ? [categoryId] : null}
            countries={country ? [country.id] : null}
            initialFilterOption={
                affinityGroupFilters?.length
                    ? { popIndexParentName: affinityGroupFilters }
                    : {}
            }
            week={week}
            year={year}
            filename={buildExportFilename({
                entity: name || 'trend',
                country,
                source: 'pop-index',
                type: 'related-category',
                kind: 'data',
                date,
            })}
            name={name}
            paginateBy="categories"
            parentEntity={category}
            parentEntityType="category"
            parentEntityId={categoryId}
            showParentNameWhenMultiple
            filterParamSeparator={filterParamSeparator}
            filterParamPrefix={filterParamPrefix}
            filtersSlot={filtersSlot}
            sortParamKey={sortParamKey}
        />
    );
}

function TikTokRelatedCategories({
    name,
    affinityGroupFilters,
    filterParamSeparator,
    filterParamPrefix,
    filtersSlot,
    sortParamKey,
}) {
    const params = useParams();
    const categoryId = params.categoryId;
    const country = useCurrentCountry();
    const { week, year } = useTikTokWeeks();
    const date = useEndDate('tiktok');

    return (
        <RelatedCategoriesSectionTable
            categories={categoryId ? [categoryId] : null}
            countries={country ? [country.id] : null}
            initialFilterOption={
                affinityGroupFilters?.length
                    ? { tikTokParentName: affinityGroupFilters }
                    : {}
            }
            week={week}
            year={year}
            filename={buildExportFilename({
                entity: name || 'trend',
                country,
                source: 'tiktok',
                type: 'related-hashtag',
                kind: 'data',
                date,
            })}
            name={name}
            filterParamSeparator={filterParamSeparator}
            filterParamPrefix={filterParamPrefix}
            filtersSlot={filtersSlot}
            sortParamKey={sortParamKey}
        />
    );
}

function InstagramRelatedCategories({
    name,
    affinityGroupFilters,
    filterParamSeparator,
    filterParamPrefix,
    filtersSlot,
    sortParamKey,
}) {
    const params = useParams();
    const categoryId = params.categoryId;
    const country = useCurrentCountry();
    const { week, year } = useInstagramWeeks();
    const date = useEndDate('instagram');

    return (
        <InstagramRelatedCategoriesSectionTable
            categories={categoryId ? [categoryId] : null}
            countries={country ? [country.id] : null}
            initialFilterOption={
                affinityGroupFilters?.length
                    ? { instagramParentName: affinityGroupFilters }
                    : {}
            }
            week={week}
            year={year}
            filename={buildExportFilename({
                entity: name || 'trend',
                country,
                source: 'instagram',
                type: 'related-category',
                kind: 'data',
                date,
            })}
            name={name}
            filterParamSeparator={filterParamSeparator}
            filterParamPrefix={filterParamPrefix}
            filtersSlot={filtersSlot}
            sortParamKey={sortParamKey}
        />
    );
}

function GoogleRelatedSearches({
    name,
    affinityGroupFilters,
    filterParamSeparator,
    filterParamPrefix,
    filtersSlot,
    sortParamKey,
}) {
    const params = useParams();
    const categoryId = params.categoryId;
    const country = useCurrentCountry();
    const { week, year } = useTikTokWeeks();
    const date = useEndDate('google');
    const controller = useAbortController(categoryId);
    return (
        <ExploreRelatedSearchesGroupTable
            countryId={country ? country.id : null}
            categoryId={categoryId || null}
            name={name}
            filename={buildExportFilename({
                entity: name || 'trend',
                country,
                source: 'google',
                type: 'related-search',
                kind: 'data',
                date,
            })}
            controller={controller}
            initialFilterOption={
                affinityGroupFilters?.length
                    ? { parentName: affinityGroupFilters }
                    : {}
            }
            showParentNameWhenMultiple
            filterParamSeparator={filterParamSeparator}
            filterParamPrefix={filterParamPrefix}
            filtersSlot={filtersSlot}
            sortParamKey={sortParamKey}
        />
    );
}

function RelatedEntityTable({
    category,
    categoryId,
    countryCode,
    dataSource,
    isNotTikTokMarket,
    name,
    affinityGroupFilters,
    normalizedAffinityGroupFilters,
    filterParamSeparator,
    filterParamPrefix,
    filtersSlot,
    sortParamKey,
}) {
    if (!categoryId) return <IndustriesPlaceholderMessage />;

    const dataSourceKey = dataSource?.toUpperCase() || DATA_SOURCES.POPULARITY;
    if (
        category &&
        dataSourceKey &&
        countryCode &&
        !isCovered({
            geo: countryCode,
            dataSource: dataSourceKey,
            cat1: category?.hierarchies?.[0]?.[1]?.cat1,
        })
    )
        return <NoDataSourceMarketCoverage dataSource={dataSourceKey} />;

    if (dataSource === 'tiktok' && !isNotTikTokMarket)
        return (
            <SectionCard css="padding-top: 1.25rem;">
                <TikTokRelatedCategories
                    name={name}
                    affinityGroupFilters={normalizedAffinityGroupFilters}
                    filterParamSeparator={filterParamSeparator}
                    filterParamPrefix={filterParamPrefix}
                    filtersSlot={filtersSlot}
                    sortParamKey={sortParamKey}
                />
            </SectionCard>
        );

    if (dataSource === 'instagram')
        return (
            <SectionCard css="padding-top: 1.25rem;">
                <InstagramRelatedCategories
                    name={name}
                    affinityGroupFilters={affinityGroupFilters}
                    filterParamSeparator={filterParamSeparator}
                    filterParamPrefix={filterParamPrefix}
                    filtersSlot={filtersSlot}
                    sortParamKey={sortParamKey}
                />
            </SectionCard>
        );

    if (!dataSource && !isNotTikTokMarket)
        return (
            <SectionCard css="padding-top: 1.25rem;">
                <PopularityIndexRelatedCategories
                    category={category}
                    name={name}
                    affinityGroupFilters={normalizedAffinityGroupFilters}
                    filterParamSeparator={filterParamSeparator}
                    filterParamPrefix={filterParamPrefix}
                    filtersSlot={filtersSlot}
                    sortParamKey={sortParamKey}
                />
            </SectionCard>
        );

    if (dataSource === 'google')
        return (
            <SectionCard css="padding-top: 1.25rem;">
                <GoogleRelatedSearches
                    name={name}
                    affinityGroupFilters={affinityGroupFilters}
                    filterParamSeparator={filterParamSeparator}
                    filterParamPrefix={filterParamPrefix}
                    filtersSlot={filtersSlot}
                    sortParamKey={sortParamKey}
                />
            </SectionCard>
        );

    if (dataSource === 'tiktok')
        return (
            <Stack $space="1.25rem">
                <NoTikTokDataSection />
            </Stack>
        );
}

function ExploreRelatedSearches() {
    const country = useCurrentCountry();
    const { mutateToWithCountryParam } = useCountryParam();
    const location = useLocation();
    const { dataSource, categoryId } = useParams();
    const searchParams = new URLSearchParams(location.search);
    const filterParamSeparator = '||';
    const filterParamPrefix = 'relatedCategories.';
    const relatedFiltersSlot = ({
        handleFilter,
        filters,
        setFilter,
        setFilters,
        queryData,
    }) => (
        <SectionTableFilterChips
            handleFilter={handleFilter}
            filters={filters}
            setFilter={setFilter}
            setFilters={setFilters}
            queryData={queryData}
            showGroupChips={false}
            showAllGroupOptions
        />
    );
    const getQueryParamKey = (source) =>
        `${filterParamPrefix}${parentNameFilterKeyForRouteSlug(source)}`;
    const parentNameParam = searchParams.get(getQueryParamKey(dataSource));
    const hasParentNameParam = Boolean(
        parentNameParam && parentNameParam.trim(),
    );
    const buildParentNameSearch = (source) => {
        if (!hasParentNameParam) return '';
        return `?${new URLSearchParams({
            [getQueryParamKey(source)]: parentNameParam,
        }).toString()}`;
    };
    const parentNameSearch = buildParentNameSearch(dataSource);
    const affinityGroups = hasParentNameParam ? [parentNameParam] : [];
    const affinityGroupFilters = affinityGroups.map((group) => ({
        label: group,
        value: group,
    }));
    const normalizedAffinityGroupFilters = affinityGroups.map((group) => {
        const normalized = String(group).toLowerCase().trim();
        return {
            label: normalized,
            value: normalized,
        };
    });

    const controller = useAbortController(categoryId);
    const categoryQuery = useEntities({
        enabled: !!categoryId,
        ids: [categoryId],
        entityType: 'categories',
        dataSource,
    });
    const category = categoryQuery.data?.[0];
    const name = category?.name || '';
    const generateLocation = ({ id }) => {
        const basePath = dataSource
            ? `/explore/related-categories/${dataSource}/trends`
            : '/explore/related-categories/trends';
        return {
            pathname: `${basePath}${id ? `/${id}` : ''}`,
            search: parentNameSearch,
        };
    };

    const isNotTikTokMarket =
        country && !TIKTOK_COUNTRIES.includes(country.text_id);

    if (!dataSource && isNotTikTokMarket && !categoryId)
        return (
            <Redirect
                to={mutateToWithCountryParam(
                    `/explore/related-categories/google/trends${categoryId ? `/${categoryId}` : ''}${buildParentNameSearch('google')}`,
                )}
            />
        );

    return (
        <PagePadding className="max-w-screen-2xl">
            <PageBar />
            <Stack $space="1.25rem">
                <Stack $space="1.5rem">
                    <div
                        css={`
                            position: relative;
                            z-index: 1;
                        `}
                    >
                        <DataSourceBreadCrumbs
                            dataSource={
                                dataSource?.toUpperCase() ||
                                DATA_SOURCES.POPULARITY
                            }
                            geo={country?.text_id}
                            cat1={category?.hierarchies?.[0]?.[1]?.cat1}
                            showIndustriesMenu={true}
                            generateLocation={generateLocation}
                            maxDepth={4}
                            replace={true}
                        />
                    </div>
                    <DataSourceTabsByCoverage
                        entityId={categoryId}
                        routePrefix="/explore"
                        hierarchyHint={category?.hierarchies?.[0]?.[1]}
                        customMode={true}
                        customOverviewTo={mutateToWithCountryParam(
                            `/explore/related-categories/trends${categoryId ? `/${categoryId}` : ''}${buildParentNameSearch()}`,
                        )}
                        customGoogleTo={mutateToWithCountryParam(
                            `/explore/related-categories/google/trends${categoryId ? `/${categoryId}` : ''}${buildParentNameSearch('google')}`,
                        )}
                        customTikTokTo={mutateToWithCountryParam(
                            `/explore/related-categories/tiktok/trends${categoryId ? `/${categoryId}` : ''}${buildParentNameSearch('tiktok')}`,
                        )}
                        customInstagramTo={mutateToWithCountryParam(
                            `/explore/related-categories/instagram/trends${categoryId ? `/${categoryId}` : ''}${buildParentNameSearch('instagram')}`,
                        )}
                    />
                    <RelatedEntityTable
                        category={category}
                        categoryId={categoryId}
                        countryCode={country?.text_id}
                        dataSource={dataSource}
                        isNotTikTokMarket={isNotTikTokMarket}
                        name={name}
                        affinityGroupFilters={affinityGroupFilters}
                        normalizedAffinityGroupFilters={
                            normalizedAffinityGroupFilters
                        }
                        filterParamSeparator={filterParamSeparator}
                        filterParamPrefix={filterParamPrefix}
                        filtersSlot={relatedFiltersSlot}
                        sortParamKey={`${filterParamPrefix}sort`}
                    />
                </Stack>
            </Stack>
        </PagePadding>
    );
}

export default ExploreRelatedSearches;
